export type ScreenSizes = 'small' | 'medium' | 'large' | 'xlarge'
export type SimpleScreenSizes = 'lessThanLarge' | 'largeOrGreater'
export type ResponsiveBehavior<T> = Record<ScreenSizes, T>
export type ResponsiveBehaviorSimple<T> = Record<SimpleScreenSizes, T>
export type ResponsiveBehaviors<K extends string, T> = Record<
	K,
	ResponsiveBehavior<T> | ResponsiveBehaviorSimple<T>
>

function includeAllProperties<T>(
	object: Record<string, T>,
	properties: string[]
): boolean {
	return Object.keys(object).every((key) => properties.includes(key))
}

export function isResponsiveBehavior<T>(
	obj: ResponsiveBehavior<T> | ResponsiveBehaviorSimple<T>
): obj is ResponsiveBehavior<T> {
	return !!(
		obj && includeAllProperties<T>(obj, ['small', 'medium', 'large', 'xlarge'])
	)
}

export function isResponsiveBehaviorSimple<T>(
	obj: ResponsiveBehavior<T> | ResponsiveBehaviorSimple<T>
): obj is ResponsiveBehaviorSimple<T> {
	return !!(
		obj && includeAllProperties<T>(obj, ['lessThanLarge', 'largeOrGreater'])
	)
}

/**
 *
 * @throws {Error} When the behavior properties are invalid
 */
export function getResponsiveBehaviors<K extends string, T>(
	behavior: ResponsiveBehaviors<K, T>,
	type: K,
	breakpoint: ScreenSizes | SimpleScreenSizes
): T {
	const behaviorType = behavior[type]
	if (isResponsiveBehavior<T>(behaviorType)) {
		return behaviorType[breakpoint as ScreenSizes]
	} else if (isResponsiveBehaviorSimple<T>(behaviorType)) {
		return behaviorType[breakpoint as SimpleScreenSizes]
	} else {
		throw new Error(
			`Invalid behavior properties ${type} ${JSON.stringify(behaviorType)}`
		)
	}
}

/**
 *
 * @throws {Error} When the behavior properties are invalid
 */
export function getResponsiveBehavior<T>(
	behavior: ResponsiveBehavior<T> | ResponsiveBehaviorSimple<T>,
	breakpoint: ScreenSizes | SimpleScreenSizes
): T | null {
	if (isResponsiveBehavior(behavior)) {
		return behavior[breakpoint as ScreenSizes]
	} else if (isResponsiveBehaviorSimple(behavior)) {
		return behavior[breakpoint as SimpleScreenSizes]
	} else {
		throw new Error('Invalid behavior properties')
	}
}
